// Material Design 3 — типографическая шкала.
// 15 уровней: display (×3) + headline (×3) + title (×3) + body (×3) + label (×3).
// Roboto — стандартный шрифт MD3, импортируется в index.html через <link>.
import type { TypographyVariantsOptions } from '@mui/material/styles';

/** Roboto как стандартный шрифт MD3. */
const FONT_FAMILY = '"Roboto", "Helvetica", "Arial", sans-serif';

/** Структура одного уровня типографики MD3. */
export interface Md3TypographyLevel {
  fontFamily: string;
  fontSize: string;
  lineHeight: string;
  fontWeight: number;
  letterSpacing?: string;
}

/** Имена 15 уровней MD3 type scale (для type-safe доступа). */
export type Md3TypographyKey =
  | 'displayLarge' | 'displayMedium' | 'displaySmall'
  | 'headlineLarge' | 'headlineMedium' | 'headlineSmall'
  | 'titleLarge' | 'titleMedium' | 'titleSmall'
  | 'bodyLarge' | 'bodyMedium' | 'bodySmall'
  | 'labelLarge' | 'labelMedium' | 'labelSmall';

/** Полная MD3 type scale — 15 уровней. */
export const md3Typography: Record<Md3TypographyKey, Md3TypographyLevel> = {
  displayLarge: { fontFamily: FONT_FAMILY, fontSize: '57px', lineHeight: '64px', fontWeight: 400, letterSpacing: '-0.25px' },
  displayMedium: { fontFamily: FONT_FAMILY, fontSize: '45px', lineHeight: '52px', fontWeight: 400 },
  displaySmall: { fontFamily: FONT_FAMILY, fontSize: '36px', lineHeight: '44px', fontWeight: 400 },
  headlineLarge: { fontFamily: FONT_FAMILY, fontSize: '32px', lineHeight: '40px', fontWeight: 400 },
  headlineMedium: { fontFamily: FONT_FAMILY, fontSize: '28px', lineHeight: '36px', fontWeight: 400 },
  headlineSmall: { fontFamily: FONT_FAMILY, fontSize: '24px', lineHeight: '32px', fontWeight: 400 },
  titleLarge: { fontFamily: FONT_FAMILY, fontSize: '22px', lineHeight: '28px', fontWeight: 400 },
  titleMedium: { fontFamily: FONT_FAMILY, fontSize: '16px', lineHeight: '24px', fontWeight: 500, letterSpacing: '0.15px' },
  titleSmall: { fontFamily: FONT_FAMILY, fontSize: '14px', lineHeight: '20px', fontWeight: 500, letterSpacing: '0.1px' },
  bodyLarge: { fontFamily: FONT_FAMILY, fontSize: '16px', lineHeight: '24px', fontWeight: 400, letterSpacing: '0.5px' },
  bodyMedium: { fontFamily: FONT_FAMILY, fontSize: '14px', lineHeight: '20px', fontWeight: 400, letterSpacing: '0.25px' },
  bodySmall: { fontFamily: FONT_FAMILY, fontSize: '12px', lineHeight: '16px', fontWeight: 400, letterSpacing: '0.4px' },
  labelLarge: { fontFamily: FONT_FAMILY, fontSize: '14px', lineHeight: '20px', fontWeight: 500, letterSpacing: '0.1px' },
  labelMedium: { fontFamily: FONT_FAMILY, fontSize: '12px', lineHeight: '16px', fontWeight: 500, letterSpacing: '0.5px' },
  labelSmall: { fontFamily: FONT_FAMILY, fontSize: '11px', lineHeight: '16px', fontWeight: 500, letterSpacing: '0.5px' },
};

/**
 * Конвертация MD3 typography в MUI `TypographyVariantsOptions` для `createTheme`.
 * Связывает md3 уровни с MUI вариантами: h1/h2/h3/h4/h5/h6 + body1/body2 + button.
 */
export function toMuiTypography(): TypographyVariantsOptions {
  return {
    fontFamily: FONT_FAMILY,
    h1: { fontSize: md3Typography.displayLarge.fontSize, lineHeight: md3Typography.displayLarge.lineHeight, fontWeight: md3Typography.displayLarge.fontWeight },
    h2: { fontSize: md3Typography.headlineLarge.fontSize, lineHeight: md3Typography.headlineLarge.lineHeight, fontWeight: md3Typography.headlineLarge.fontWeight },
    h3: { fontSize: md3Typography.headlineMedium.fontSize, lineHeight: md3Typography.headlineMedium.lineHeight, fontWeight: md3Typography.headlineMedium.fontWeight },
    h4: { fontSize: md3Typography.titleLarge.fontSize, lineHeight: md3Typography.titleLarge.lineHeight, fontWeight: md3Typography.titleLarge.fontWeight },
    h5: { fontSize: md3Typography.titleMedium.fontSize, lineHeight: md3Typography.titleMedium.lineHeight, fontWeight: md3Typography.titleMedium.fontWeight },
    h6: { fontSize: md3Typography.titleSmall.fontSize, lineHeight: md3Typography.titleSmall.lineHeight, fontWeight: md3Typography.titleSmall.fontWeight },
    body1: { fontSize: md3Typography.bodyLarge.fontSize, lineHeight: md3Typography.bodyLarge.lineHeight },
    body2: { fontSize: md3Typography.bodyMedium.fontSize, lineHeight: md3Typography.bodyMedium.lineHeight },
    button: { fontSize: md3Typography.labelLarge.fontSize, lineHeight: md3Typography.labelLarge.lineHeight, textTransform: 'none' },
  };
}
